import AddIcon from '@mui/icons-material/Add';
import { Box, Button, Stack } from '@mui/material';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useAuthContext } from '../../../../../context/auth';
import { errorToast, successToast } from '../../../../Toast';
import { TenantsApi } from '../../../namespace/api';
import IntegrationsAPI, {
	AutofillCallResponse,
	AutofillIntegrationCreateRequest,
	ExternalServiceIntegration,
	ExternalServiceIntegrationAddRequest,
	ExternalServiceIntegrationUpdateRequest,
} from '../../api/integrations';
import {
	ADD_AUTOFILL_SEQUENCE_FAILURE,
	ADD_AUTOFILL_SEQUENCE_SUCCESS,
	ADD_INTEGRATION_FAILURE,
	ADD_INTEGRATION_SUCCESS,
	AUTOFILL_SEQUENCE_LABEL,
	DATA_AUTOFILL_INTEGRATION_LABEL,
	DATA_AUTOFILL_TYPE,
	DELETE_AUTOFILL_SEQUENCE_FAILURE,
	DELETE_AUTOFILL_SEQUENCE_SUCCESS,
	DELETE_INTEGRATION_FAILURE,
	DELETE_INTEGRATION_SUCCESS,
	EDIT_AUTOFILL_SEQUENCE_FAILURE,
	EDIT_AUTOFILL_SEQUENCE_SUCCESS,
	EDIT_INTEGRATION_FAILURE,
	EDIT_INTEGRATION_SUCCESS,
	NEW_LABEL,
} from '../../data/constants';
import AutofillSequencesTable from './AutofillSequencesTable';
import IntegrationCard from './IntegrationCard';
import IntegrationSummary from './IntegrationSummary';
import AddUpdateAutofillSequenceModal from './modals/AddUpdateAutofillSequenceModal';
import AddUpdateIntegrationModal from './modals/AddUpdateIntegrationModal';
import ConfirmDeleteAutofillSequenceModal from './modals/ConfirmDeleteAutofillSequenceModal';
import ConfirmDeleteIntegrationModal from './modals/ConfirmDeleteIntegrationModal';

export default function DataAutofillsPanel() {
	const { tenantHostname } = useParams();
	const client = useQueryClient();
	const authData = useAuthContext();
	const authToken = authData?.tenantAuthorizationData?.authorizationToken;
	const tenantId = authData?.tenantAuthorizationData?.tenantId;
	// show/hide modals
	const [showIntegrationModal, setShowIntegrationModal] = useState(false);
	const [showAutofillSequenceModal, setShowAutofillSequenceModal] =
		useState(false);
	const [
		showConfirmDeleteIntegrationModal,
		setShowConfirmDeleteIntegrationModal,
	] = useState(false);
	const [
		showConfirmDeleteAutofillSequenceModal,
		setShowConfirmDeleteAutofillSequenceModal,
	] = useState(false);

	// selected integration/autofill sequence for editing/deleting
	const [selectedIntegration, setSelectedIntegration] =
		useState<ExternalServiceIntegration>(null);
	const [selectedAutofillSequence, setSelectedAutofillSequence] =
		useState<AutofillCallResponse>(null);

	// Data
	const autofillSequencesQueryKey = [
		'autofillSequences',
		tenantId || tenantHostname,
	];
	const autofillIntegrationsQueryKey = [
		'autofillIntegrations',
		tenantId || tenantHostname,
	];
	const { data: autofillSequences } = IntegrationsAPI.useDataAutofillSequences(
		autofillSequencesQueryKey,
		authToken,
		tenantHostname,
	);
	const { data: autofillIntegrations } =
		IntegrationsAPI.useDataAutofillIntegrations(
			autofillIntegrationsQueryKey,
			authToken,
			tenantHostname,
		);

	// Integration Mutations
	const {
		mutate: addAutofillIntegration,
		isLoading: isAddingAutofillIntegration,
	} = useMutation(
		(newExternalRater: ExternalServiceIntegrationAddRequest) => {
			return tenantHostname
				? TenantsApi.addIntegration(newExternalRater, tenantHostname)
				: IntegrationsAPI.addIntegration(newExternalRater, authToken);
		},
		{
			onSuccess: () => {
				client.invalidateQueries(autofillIntegrationsQueryKey);
				successToast(ADD_INTEGRATION_SUCCESS());
				setShowIntegrationModal(false);
				setSelectedIntegration(null);
			},
			onError: (err) => {
				if (axios.isAxiosError(err) && err.response.data.message) {
					errorToast(
						ADD_INTEGRATION_FAILURE() + ': ' + err.response.data.message,
					);
				} else {
					errorToast(ADD_INTEGRATION_FAILURE());
				}
			},
		},
	);

	const {
		mutate: updateAutofillIntegration,
		isLoading: isUpdatingAutofillIntegration,
	} = useMutation(
		(updatedExternalRater: ExternalServiceIntegrationUpdateRequest) => {
			return tenantHostname
				? TenantsApi.editIntegration(
						updatedExternalRater,
						selectedIntegration.name,
						tenantHostname,
				  )
				: IntegrationsAPI.editIntegration(
						updatedExternalRater,
						selectedIntegration.name,
						authToken,
				  );
		},
		{
			onSuccess: () => {
				client.invalidateQueries(autofillIntegrationsQueryKey);
				successToast(EDIT_INTEGRATION_SUCCESS());
				setShowIntegrationModal(false);
				setSelectedIntegration(null);
			},
			onError: (err) => {
				if (axios.isAxiosError(err) && err.response.data.message) {
					errorToast(
						EDIT_INTEGRATION_FAILURE() + ': ' + err.response.data.message,
					);
				} else {
					errorToast(EDIT_INTEGRATION_FAILURE());
				}
			},
		},
	);

	const {
		mutate: deleteAutofillIntegration,
		isLoading: isDeletingAutofillIntegration,
	} = useMutation(
		(integration: ExternalServiceIntegration) => {
			return tenantHostname
				? TenantsApi.deleteIntegration(integration.name, tenantHostname)
				: IntegrationsAPI.deleteIntegration(integration.name, authToken);
		},
		{
			onSuccess: () => {
				client.invalidateQueries(autofillIntegrationsQueryKey);
				successToast(DELETE_INTEGRATION_SUCCESS());
			},
			onError: (err) => {
				if (axios.isAxiosError(err) && err.response.data.message) {
					errorToast(
						DELETE_INTEGRATION_FAILURE() + ': ' + err.response.data.message,
					);
				} else {
					errorToast(DELETE_INTEGRATION_FAILURE());
				}
			},
			onSettled: () => {
				// close delete modal on success or error; not losing any data/user input
				setShowConfirmDeleteIntegrationModal(false);
				setSelectedIntegration(null);
			},
		},
	);

	// Sequence Mutations
	const {
		mutate: addUpdateAutofillSequence,
		isLoading: isAddingOrUpdatingAutofillSequence,
	} = useMutation(
		(newAutofillSequence: AutofillIntegrationCreateRequest) => {
			return tenantHostname
				? TenantsApi.addUpdateDataAutofill(newAutofillSequence, tenantHostname)
				: IntegrationsAPI.addUpdateDataAutofill(newAutofillSequence, authToken);
		},
		{
			onSuccess: () => {
				client.invalidateQueries(autofillSequencesQueryKey);
				successToast(
					!selectedAutofillSequence
						? ADD_AUTOFILL_SEQUENCE_SUCCESS()
						: EDIT_AUTOFILL_SEQUENCE_SUCCESS(),
				);
				setShowAutofillSequenceModal(false);
				setSelectedAutofillSequence(null);
			},
			onError: (err) => {
				if (axios.isAxiosError(err) && err.response.data.message) {
					errorToast(
						!selectedAutofillSequence
							? ADD_AUTOFILL_SEQUENCE_FAILURE()
							: EDIT_AUTOFILL_SEQUENCE_FAILURE() +
									': ' +
									err.response.data.message,
					);
				} else {
					errorToast(
						!selectedAutofillSequence
							? ADD_AUTOFILL_SEQUENCE_FAILURE()
							: EDIT_AUTOFILL_SEQUENCE_FAILURE(),
					);
				}
			},
		},
	);

	const {
		mutate: deleteAutofillSequence,
		isLoading: isDeletingAutofillSequence,
	} = useMutation(
		(autofillSequenceLocator: string) => {
			return tenantHostname
				? TenantsApi.deleteDataAutofill(autofillSequenceLocator, tenantHostname)
				: IntegrationsAPI.deleteDataAutofill(
						autofillSequenceLocator,
						authToken,
				  );
		},
		{
			onSuccess: () => {
				client.invalidateQueries(autofillSequencesQueryKey);
				successToast(DELETE_AUTOFILL_SEQUENCE_SUCCESS());
			},
			onError: (err) => {
				if (axios.isAxiosError(err) && err.response.data.message) {
					errorToast(
						DELETE_AUTOFILL_SEQUENCE_FAILURE() +
							': ' +
							err.response.data.message,
					);
				} else {
					errorToast(DELETE_AUTOFILL_SEQUENCE_FAILURE());
				}
			},
			onSettled: () => {
				setShowConfirmDeleteAutofillSequenceModal(false);
				setSelectedAutofillSequence(null);
			},
		},
	);

	// Loading
	const isLoading =
		isAddingAutofillIntegration ||
		isUpdatingAutofillIntegration ||
		isDeletingAutofillIntegration ||
		isAddingOrUpdatingAutofillSequence ||
		isDeletingAutofillSequence;

	// Autofill integration API functions
	const handleAddIntegration = (integration: ExternalServiceIntegration) => {
		addAutofillIntegration(integration);
	};

	const handleEditIntegration = (integration: ExternalServiceIntegration) => {
		updateAutofillIntegration(integration);
	};

	const handleDeleteIntegration = (integration: ExternalServiceIntegration) => {
		deleteAutofillIntegration(integration);
	};

	// Autofill sequence API functions
	const handleAddUpdateDataAutofillSequence = (
		autofillSequence: AutofillCallResponse,
	) => {
		addUpdateAutofillSequence(autofillSequence);
	};

	const handleDeleteDataAutofillSequence = (
		autofillSequence: AutofillCallResponse,
	) => {
		deleteAutofillSequence(autofillSequence.locator);
	};

	// Modals
	const handleOpenAddIntegrationModal = () => {
		setSelectedIntegration(null);
		setShowIntegrationModal(true);
	};

	const handleOpenEditIntegrationModal = (
		integration: ExternalServiceIntegration,
	) => {
		setSelectedIntegration(integration);
		setShowIntegrationModal(true);
	};

	const handleOpenConfirmDeleteIntegrationModal = (
		integration: ExternalServiceIntegration,
	) => {
		setSelectedIntegration(integration);
		setShowConfirmDeleteIntegrationModal(true);
	};

	const handleOpenAddAutofillSequenceModal = () => {
		setSelectedAutofillSequence(null);
		setShowAutofillSequenceModal(true);
	};

	const handleOpenEditAutofillSequenceModal = (
		autofillSequence: AutofillCallResponse,
	) => {
		setSelectedAutofillSequence(autofillSequence);
		setShowAutofillSequenceModal(true);
	};

	const handleOpenConfirmDeleteSequenceModal = (
		autofillSequence: AutofillCallResponse,
	) => {
		setSelectedAutofillSequence(autofillSequence);
		setShowConfirmDeleteAutofillSequenceModal(true);
	};

	const onCancel = () => {
		setSelectedIntegration(null);
		setSelectedAutofillSequence(null);
		setShowIntegrationModal(false);
		setShowConfirmDeleteIntegrationModal(false);
		setShowConfirmDeleteAutofillSequenceModal(false);
	};

	return (
		<>
			<Box
				sx={{
					height: '100%',
					display: 'grid',
					gap: 2,
					gridTemplateColumns: '266px 1fr',
					overflow: 'hidden',
					pb: 2,
				}}
			>
				<Box sx={{ flex: 1, overflowY: 'auto' }}>
					<Stack direction='column' spacing={2} sx={{ maxHeight: '100%' }}>
						<Button
							variant='contained'
							color='primary'
							sx={{ width: 'fit-content' }}
							startIcon={<AddIcon />}
							onClick={handleOpenAddIntegrationModal}
						>
							{NEW_LABEL(DATA_AUTOFILL_INTEGRATION_LABEL)}
						</Button>
						<IntegrationSummary
							title={DATA_AUTOFILL_INTEGRATION_LABEL(true)}
							count={autofillIntegrations?.length}
						/>

						{autofillIntegrations?.map((integration, i) => (
							<IntegrationCard
								integration={integration}
								key={DATA_AUTOFILL_INTEGRATION_LABEL() + i}
								onEdit={handleOpenEditIntegrationModal}
								onDelete={handleOpenConfirmDeleteIntegrationModal}
							/>
						))}
					</Stack>
				</Box>

				<Stack
					direction='column'
					spacing={2}
					sx={{ height: '100%', overflowY: 'auto' }}
				>
					<Button
						variant='contained'
						color='primary'
						sx={{ width: 'fit-content' }}
						startIcon={<AddIcon />}
						onClick={handleOpenAddAutofillSequenceModal}
					>
						{NEW_LABEL(AUTOFILL_SEQUENCE_LABEL)}
					</Button>
					<IntegrationSummary
						width={266}
						title={AUTOFILL_SEQUENCE_LABEL(true)}
						count={autofillSequences?.calls?.length}
					/>
					{autofillSequences?.calls?.length ? (
						<AutofillSequencesTable
							autofillSequences={autofillSequences?.calls}
							onEdit={handleOpenEditAutofillSequenceModal}
							onDelete={handleOpenConfirmDeleteSequenceModal}
						/>
					) : null}
				</Stack>
			</Box>
			{showAutofillSequenceModal ? (
				<AddUpdateAutofillSequenceModal
					open={showAutofillSequenceModal}
					autofillSequence={selectedAutofillSequence}
					autofillIntegrations={autofillIntegrations}
					onAddUpdate={handleAddUpdateDataAutofillSequence}
					onCancel={() => setShowAutofillSequenceModal(false)}
					isLoading={isLoading}
				/>
			) : null}
			{showConfirmDeleteAutofillSequenceModal ? (
				<ConfirmDeleteAutofillSequenceModal
					open={showConfirmDeleteAutofillSequenceModal}
					autofillSequence={selectedAutofillSequence}
					onCancel={onCancel}
					onDelete={handleDeleteDataAutofillSequence}
					isLoading={isLoading}
				/>
			) : null}
			{showIntegrationModal ? (
				<AddUpdateIntegrationModal
					integrationNames={
						autofillIntegrations
							? autofillIntegrations.map((integration) => integration.name)
							: []
					}
					open={showIntegrationModal}
					integrationType={DATA_AUTOFILL_TYPE}
					integrationTypeLabel={DATA_AUTOFILL_INTEGRATION_LABEL}
					integration={selectedIntegration}
					onCancel={onCancel}
					onAdd={handleAddIntegration}
					onEdit={handleEditIntegration}
					isLoading={isLoading}
				/>
			) : null}
			{showConfirmDeleteIntegrationModal ? (
				<ConfirmDeleteIntegrationModal
					open={showConfirmDeleteIntegrationModal}
					integration={selectedIntegration}
					integrationTypeLabel={DATA_AUTOFILL_INTEGRATION_LABEL}
					onCancel={onCancel}
					onDelete={handleDeleteIntegration}
					isLoading={isLoading}
				/>
			) : null}
		</>
	);
}
